import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ServiceFormatter } from '../../helpers/service-formatter';
import { HccModelVersion } from '../../models/hcc-model-version';
import { FilterSelectItem } from '../../models/filter-select-item';
import { ReplaySubject } from 'rxjs';
import { OrgMapsFileTypesResult } from '../../models/org-maps-file-types-result';
import { ModelVersionResult } from '../../models/model-version-result';
import { ExportTypeApiItem } from '../../models/export-type';

@Injectable({
  providedIn: 'root'
})
export class FieldFilterService {
  private endpoint = `${environment.baseURL}/api/`;

  templateConfigTypes: ReplaySubject<FilterSelectItem[]>;
  opptyPreferredActions: ReplaySubject<FilterSelectItem[]>;

  constructor(private http: HttpClient) { 
    this.templateConfigTypes = new ReplaySubject<FilterSelectItem[]>(1);
    this.opptyPreferredActions = new ReplaySubject<FilterSelectItem[]>(1);
  }

  getStatusStateOptions() {
    return this.http.get<any>(this.endpoint + "filters/status-state", ServiceFormatter.getHttpOptions());
  }

  getStatusOptions(dxEventTypeIDs: string, stateIds: number[], isEndUserSelect: number = 1) {
    const params = new HttpParams()
    .set('dxEventTypeIDs', dxEventTypeIDs)
    .set('stateIds', stateIds.toString())
    .set('isEndUserSelect', isEndUserSelect.toString());

    return this.http.get<any>(this.endpoint + "filters/status", ServiceFormatter.getHttpOptions(params));
  }

  getRootCauseOptions(statusIds: number[]) {
    const params = new HttpParams().set('statusIds', statusIds.toString());
    return this.http.get<any>(this.endpoint + "filters/root-cause", ServiceFormatter.getHttpOptions(params));
  }

  getPreferredActionOptions(typeIds: string) {
    const params = new HttpParams().set('typeIds', typeIds);
    return this.http.get<any>(this.endpoint + "filters/preferred-actions", ServiceFormatter.getHttpOptions(params));
  }

  getOpptyPreferredActionOptionsSubject() {
    const params = new HttpParams().set('typeIds', '1');
    return this.http.get<any>(this.endpoint + "filters/preferred-actions", ServiceFormatter.getHttpOptions(params)).subscribe(result => {
      this.opptyPreferredActions.next(result);
    });
  }

  getPayerOptions(includeInactive = false) {
    const params = new HttpParams().set('includeInactive', includeInactive);
    return this.http.get<any>(this.endpoint + "filters/payers", ServiceFormatter.getHttpOptions(params));
  }

  getRiskScoreRunTypeOptions() {
    return this.http.get<any>(this.endpoint + "filters/risk-score-run-types", ServiceFormatter.getHttpOptions());
  }

  getUserRoles() {
    return this.http.get<any>(this.endpoint + "filters/user-roles", ServiceFormatter.getHttpOptions());
  }

  getTimezones() {
    return this.http.get<any>(this.endpoint + "filters/time-zones", ServiceFormatter.getHttpOptions());
  }

  getHccModelCategories() {
    return this.http.get<any>(this.endpoint + "filters/hcc-model-categories", ServiceFormatter.getHttpOptions());
  }

  getHccModelVersionsByCategory(categoryIDs: number[]) {
    const params = new HttpParams().set('categoryIDs', categoryIDs.toString());
    return this.http.get<HccModelVersion[]>(this.endpoint + "filters/hcc-model-versions-category", ServiceFormatter.getHttpOptions(params));
  }

  getActiveAdminAuditActions() {
    return this.http.get<any>(this.endpoint + "filters/admin-audit-actions", ServiceFormatter.getHttpOptions());
  }

  getTemplateConfigTypes() {
    return this.http.get<any>(this.endpoint + "filters/export-template-types", ServiceFormatter.getHttpOptions()).subscribe(result => {
      this.templateConfigTypes.next(result);
    });
  }

  getExportTemplatesByTypeAndPayer(typeIDs: string, payerIDs?: string, startDate?: string, endDate?: string) {
    const params = new HttpParams()
    .set('typeIDs', typeIDs)
    .set('payerIDs', payerIDs || '')
    .set('startDate', startDate || '')
    .set('endDate', endDate || '');
    return this.http.get<any>(this.endpoint + "filters/export-templates", ServiceFormatter.getHttpOptions(params));
  }

  getMORPayerOptions() {
    return this.http.get<any>(this.endpoint + "filters/mor-payers", ServiceFormatter.getHttpOptions());
  }

  getAvailableMapsFileTypesByOrg() {
    return this.http.get<OrgMapsFileTypesResult[]>(`${this.endpoint}filters/org-maps-file-types`, ServiceFormatter.getHttpOptions());
  }

  getMapFileTypesByPayerId(payerIds: string) {
    const params = new HttpParams()
      .set('payerIds', payerIds);
    return this.http.get<OrgMapsFileTypesResult[]>(`${this.endpoint}filters/payer-maps-file-types`, ServiceFormatter.getHttpOptions(params));
  }

  getModelVersionDropdownItemsByFileType(fileTypeId: number) {
    const params = new HttpParams()
      .set('fileTypeId', fileTypeId.toString());
    return this.http.get<ModelVersionResult[]>(`${this.endpoint}filters/model-versions`, ServiceFormatter.getHttpOptions(params));
  }

  getASMPayerOptions(serviceYear: number) {
    const params = new HttpParams().set('serviceYear', serviceYear.toString());
    return this.http.get<any>(`${this.endpoint}filters/asm-payers`, ServiceFormatter.getHttpOptions(params));
  }

  getExportTypeOptions() {
    return this.http.get<ExportTypeApiItem[]>(`${this.endpoint}filters/export-types`, ServiceFormatter.getHttpOptions());
  }
}
